import * as THREE from 'three';
import type { AudioHooks } from '../audio/audio';
import type { Sim } from '../sim/sim';
import type { Particles } from './effects';

/**
 * Spawn-in entrances (roadmap #16). Purely cosmetic: the sim holds everyone
 * for `RunConfig.introSeconds` (control starts on the same tick everywhere),
 * and this animates each player's avatar over that window. The entrance is
 * picked from the run seed + player index, so co-op screens agree without any
 * sim state. Reduced flash / reduced motion always uses the calm fade-in.
 */

export type EntranceKind = 'skyfall' | 'teleport' | 'smoke' | 'burrow' | 'vehicle';
export const ENTRANCES: readonly EntranceKind[] = ['skyfall', 'teleport', 'smoke', 'burrow', 'vehicle'];

export function entranceFor(seed: number, player: number, reduced: boolean): EntranceKind | 'fade' {
  if (reduced) return 'fade';
  return ENTRANCES[(((seed >>> 0) % 997) + player * 3) % ENTRANCES.length];
}

interface AvatarLike {
  readonly group: THREE.Group;
}

interface EntranceState {
  kind: EntranceKind | 'fade';
  fired: Set<string>;
  prop: THREE.Object3D | null;
}

const SKY_HEIGHT = 14;
const EARTH = 0x8a6a45;

export class Entrances {
  private states: EntranceState[] = [];
  private runKey = '';

  constructor(private scene: THREE.Scene, private particles: Particles, private audio: AudioHooks, private shake: (strength: number) => void) {}

  /** Call after the avatars' own update each frame. */
  apply(sim: Sim | null, avatars: readonly AvatarLike[], reduced: boolean): void {
    const total = sim?.config.introSeconds ?? 0;
    const left = sim?.run.introT ?? 0;
    if (!sim || total <= 0) {
      this.reset(avatars);
      return;
    }
    const key = `${sim.config.seed}:${sim.config.playerCount}`;
    if (key !== this.runKey) {
      this.reset(avatars);
      this.runKey = key;
      this.states = avatars.map((_, i) => ({ kind: entranceFor(sim.config.seed, i, reduced), fired: new Set(), prop: null }));
    }
    if (left <= 0) {
      this.finish(avatars);
      return;
    }
    const p = 1 - left / total;
    const eraColor = sim.currentEra.palette.accent;
    avatars.forEach((avatar, i) => {
      const st = this.states[i];
      if (st) this.animate(st, avatar.group, p, eraColor, sim.currentEra.index);
    });
  }

  private animate(st: EntranceState, g: THREE.Group, p: number, eraColor: number, eraIndex: number): void {
    const x = g.position.x;
    const z = g.position.z;
    const once = (id: string, fn: () => void) => { if (!st.fired.has(id)) { st.fired.add(id); fn(); } };
    g.scale.set(1, 1, 1);
    switch (st.kind) {
      case 'fade': {
        const s = smooth(Math.min(1, p / 0.6));
        g.visible = p > 0.05;
        g.scale.setScalar(0.6 + 0.4 * s);
        break;
      }
      case 'skyfall': {
        const fall = Math.min(1, p / 0.5);
        if (p < 0.5) {
          g.position.y = SKY_HEIGHT * (1 - fall * fall);
          g.rotation.x = (1 - fall) * 0.6;
        } else {
          g.position.y = 0;
          g.rotation.x = 0;
          const squash = p < 0.75 ? 1 - 0.4 * (1 - (p - 0.5) / 0.25) : 1;
          g.scale.set(1 / Math.sqrt(squash), squash, 1 / Math.sqrt(squash));
          once('impact', () => {
            this.particles.burst(x, z, 46, EARTH, 7, 0.6, 0.22, 2.5);
            this.particles.burst(x, z, 18, 0xffffff, 4, 0.35, 0.12, 5);
            this.shake(0.5);
            this.audio.play('stomp');
          });
        }
        break;
      }
      case 'teleport': {
        const beam = this.prop(st, () => makeBeam(eraColor)) as THREE.Mesh<THREE.CylinderGeometry, THREE.ShaderMaterial>;
        beam.position.set(x, 0, z);
        const glow = p < 0.3 ? p / 0.3 : p < 0.6 ? 1 : Math.max(0, 1 - (p - 0.6) / 0.35);
        beam.material.uniforms.uOpacity.value = 1.4 * glow;
        beam.material.uniforms.uTime.value = p * 4;
        beam.scale.set(0.4 + 0.6 * glow, 1, 0.4 + 0.6 * glow);
        g.visible = p > 0.42 && !(p < 0.6 && Math.floor(p * 60) % 2 === 0);
        once('arrive', () => this.audio.play('blink'));
        if (p > 0.42) once('spark', () => this.particles.burst(x, z, 36, eraColor, 5, 0.5, 0.14, 6));
        break;
      }
      case 'smoke': {
        g.visible = p > 0.45;
        if (p > 0.3) once('puff', () => {
          this.particles.burst(x, z, 60, 0x9aa3ad, 3.2, 0.9, 0.35, 2.2);
          this.particles.burst(x, z, 20, 0xe6f2f5, 2, 0.7, 0.28, 3.5);
          this.audio.play('rewind');
        });
        if (p > 0.45) g.scale.setScalar(Math.min(1, 0.7 + (p - 0.45) * 2));
        break;
      }
      case 'burrow': {
        const mound = this.prop(st, () => makeMound());
        mound.position.set(x, 0.02, z);
        mound.scale.setScalar(p < 0.3 ? p / 0.3 : Math.max(0.001, 1 - (p - 0.75) / 0.25));
        if (p < 0.3) { g.visible = false; break; }
        g.visible = true;
        if (p < 0.75) g.position.y = -1.8 * (1 - smooth((p - 0.3) / 0.45));
        else g.position.y = 0.7 * Math.sin(Math.min(1, (p - 0.75) / 0.2) * Math.PI);
        once('break', () => { this.particles.burst(x, z, 40, EARTH, 5, 0.6, 0.2, 4); this.audio.play('emerge'); });
        if (p > 0.75) once('pop', () => this.particles.burst(x, z, 24, EARTH, 6, 0.45, 0.16, 5));
        break;
      }
      case 'vehicle': {
        const craft = this.prop(st, () => makeCraft(eraColor, eraIndex));
        // Arrive (0–0.45), idle while the rider hops out (0.45–0.65), leave (0.65–1).
        if (p < 0.45) {
          const t = smooth(p / 0.45);
          craft.position.set(x - 14 * (1 - t) + 1.4 * t, 9 * (1 - t) + 0.6 * t, z + 6 * (1 - t));
          craft.rotation.set(0, -0.6 * (1 - t), 0.25 * (1 - t));
        } else if (p < 0.65) {
          craft.position.set(x + 1.4, 0.6 + 0.05 * Math.sin(p * 60), z);
          craft.rotation.set(0, 0, 0);
        } else {
          const t = (p - 0.65) / 0.35;
          craft.position.set(x + 1.4 + 16 * t * t, 0.6 + 10 * t * t, z - 4 * t);
          craft.rotation.set(0, 0.4 * t, -0.3 * t);
        }
        g.visible = p > 0.5;
        if (p > 0.5 && p < 0.65) g.position.y = 0.8 * Math.sin(((p - 0.5) / 0.15) * Math.PI);
        if (p > 0.45) once('land', () => { this.particles.burst(x + 1.4, z, 30, 0xd8d0c0, 4, 0.5, 0.2, 1.5); this.audio.play('dash'); });
        if (p > 0.65) once('leave', () => this.particles.burst(x + 1.4, z, 20, eraColor, 5, 0.4, 0.15, 3));
        break;
      }
    }
  }

  private prop(st: EntranceState, make: () => THREE.Object3D): THREE.Object3D {
    if (!st.prop) {
      st.prop = make();
      this.scene.add(st.prop);
    }
    return st.prop;
  }

  private finish(avatars: readonly AvatarLike[]): void {
    for (const st of this.states) this.dropProp(st);
    for (const a of avatars) {
      a.group.scale.set(1, 1, 1);
      a.group.rotation.x = 0;
      a.group.position.y = 0;
    }
  }

  private reset(avatars: readonly AvatarLike[]): void {
    if (this.states.length) this.finish(avatars);
    this.states = [];
    this.runKey = '';
  }

  private dropProp(st: EntranceState): void {
    if (!st.prop) return;
    this.scene.remove(st.prop);
    st.prop.traverse((o) => {
      const mesh = o as THREE.Mesh;
      mesh.geometry?.dispose();
      const mat = mesh.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat?.dispose();
    });
    st.prop = null;
  }
}

function smooth(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

/** Light column: bright rim, hollow core, fading toward the sky, with scrolling bands. */
function makeBeam(color: number): THREE.Mesh<THREE.CylinderGeometry, THREE.ShaderMaterial> {
  const geo = new THREE.CylinderGeometry(0.9, 0.9, 16, 32, 1, true);
  geo.translate(0, 8, 0);
  const material = new THREE.ShaderMaterial({
    uniforms: { uColor: { value: new THREE.Color(color) }, uOpacity: { value: 0 }, uTime: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv; varying float vRim;
      void main() {
        vUv = uv;
        vec3 n = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vRim = 1.0 - abs(dot(n, normalize(-mv.xyz)));
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor; uniform float uOpacity; uniform float uTime;
      varying vec2 vUv; varying float vRim;
      void main() {
        float fade = pow(1.0 - vUv.y, 1.6);
        float rim = 0.15 + 0.85 * pow(vRim, 2.0);
        float bands = 0.75 + 0.25 * sin(vUv.y * 40.0 - uTime * 12.0);
        float a = fade * rim * bands * uOpacity;
        gl_FragColor = vec4(mix(uColor, vec3(1.0), 0.35 * rim) * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
  });
  return new THREE.Mesh(geo, material);
}

function makeMound(): THREE.Mesh {
  const geo = new THREE.SphereGeometry(1, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2);
  geo.scale(1.1, 0.35, 1.1);
  return new THREE.Mesh(geo, new THREE.MeshToonMaterial({ color: EARTH }));
}

/** Era-flavored drop-off craft built from primitives: organic pod → chariot → airship → starship. */
function makeCraft(accent: number, era: number): THREE.Group {
  const g = new THREE.Group();
  const hull = new THREE.MeshToonMaterial({ color: era < 3 ? 0x3f8f7a : era < 6 ? 0x8a5a2b : era < 8 ? 0x6b5d4a : 0x4a5563 });
  const glow = new THREE.MeshBasicMaterial({ color: accent });
  if (era < 3) {
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.9, 18, 12), hull);
    body.scale.set(1.3, 0.8, 1);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 8), glow);
    eye.position.set(0.9, 0.2, 0);
    g.add(body, eye);
  } else if (era < 6) {
    const box = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.6, 1.1), hull);
    g.add(box);
    for (const [wx, wz] of [[-0.6, 0.6], [0.6, 0.6], [-0.6, -0.6], [0.6, -0.6]]) {
      const wheel = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.08, 8, 16), glow);
      wheel.position.set(wx, -0.3, wz);
      g.add(wheel);
    }
  } else if (era < 8) {
    const balloon = new THREE.Mesh(new THREE.CapsuleGeometry(0.6, 1.4, 6, 12), hull);
    balloon.rotation.z = Math.PI / 2;
    balloon.position.y = 0.9;
    const gondola = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.35, 0.6), new THREE.MeshToonMaterial({ color: 0x3a2a1a }));
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), glow);
    lamp.position.set(0.5, 0, 0);
    g.add(balloon, gondola, lamp);
  } else {
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.55, 2.2, 14), hull);
    nose.rotation.z = -Math.PI / 2;
    const wing = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.08, 2.2), hull);
    wing.position.x = -0.3;
    const engine = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), glow);
    engine.position.x = -1.1;
    g.add(nose, wing, engine);
  }
  return g;
}
